/**
 * County-aware service-area matching helpers (web client).
 *
 * The canonical county↔city dataset lives on the API; the server pre-computes
 * a `serviceAreaCountyKeys: string[]` array next to every `serviceAreas: string[]`
 * field it returns (e.g. on partner suggestions). For the current user, the
 * client calls `POST /api/locations/resolve-county-keys` once and caches the
 * result.
 *
 * A "county-key" is `${countyLower}|${stateCodeUpper}` (e.g. `fulton|GA`). Two
 * service-area sets overlap when they share at least one key, OR — as a safety
 * fallback for off-dataset hand-typed entries — when their lowercased strings
 * intersect.
 */

export type CountyKey = string;

export function lowercaseAreas(areas: string[] | null | undefined): string[] {
  if (!Array.isArray(areas)) return [];
  return areas.map((a) => a.trim().toLowerCase()).filter(Boolean);
}

/**
 * True if any county-key in `partnerKeys` is also in `userKeys`. Either input
 * may be empty; an empty input means "no resolved counties" and the helper
 * returns false (use {@link serviceAreasOverlap} for full city-string fallback).
 */
export function countyKeysOverlap(
  userKeys: CountyKey[] | null | undefined,
  partnerKeys: CountyKey[] | null | undefined,
): boolean {
  if (!Array.isArray(userKeys) || !Array.isArray(partnerKeys)) return false;
  if (userKeys.length === 0 || partnerKeys.length === 0) return false;
  const set = new Set(userKeys);
  for (const k of partnerKeys) {
    if (set.has(k)) return true;
  }
  return false;
}

/**
 * Full overlap check: county-keys first (so a county selection matches any
 * city inside it), with a case-insensitive string fallback for any entry the
 * server couldn't resolve against the reference dataset.
 */
export function serviceAreasOverlap(opts: {
  userKeys?: CountyKey[] | null;
  userAreas?: string[] | null;
  partnerKeys?: CountyKey[] | null;
  partnerAreas?: string[] | null;
}): boolean {
  if (countyKeysOverlap(opts.userKeys, opts.partnerKeys)) return true;

  const userLower = lowercaseAreas(opts.userAreas);
  const partnerLower = lowercaseAreas(opts.partnerAreas);
  if (userLower.length === 0 || partnerLower.length === 0) return false;
  const set = new Set(userLower);
  return partnerLower.some((a) => set.has(a));
}
